/**
 * Guia → Markdown (para wikis, Notion, GitHub…). As imagens são ficheiros ao lado
 * (images/01.jpg) num ZIP; o texto das pessoas é escapado para não virar formatação.
 */
import { cleanUrl } from '../api';
import {
  actionCount,
  displayUrl,
  estimatedMinutes,
  stepNumbers,
  stepTitle,
  type Guide,
} from '../steps';
import { guideLocale, guideText } from '../guide-text';

/** Escapa o que o Markdown interpretaria (ênfase, ligações, títulos, HTML). */
export function mdEscape(s: string): string {
  return s
    .replace(/([\\`*_{}[\]()#+!|<>~])/g, '\\$1')
    .replace(/^(\s*)(\d+)\./gm, '$1$2\\.')
    .replace(/^(\s*)-(\s)/gm, '$1\\-$2')
    .replace(/^(\s*)([-=]{3,})/gm, '$1\\$2');
}

const lines = (s: string) => mdEscape(s.trim()).replace(/\n/g, '  \n');

/** images: id do passo → caminho relativo da imagem no ZIP (ou null). */
export function buildMarkdown(
  guide: Pick<Guide, 'title' | 'description' | 'steps' | 'language'>,
  images: Record<string, string | null>,
): string {
  const out: string[] = [];
  const numbers = stepNumbers(guide);
  const count = actionCount(guide);
  const L = guideLocale(guide);
  const t = guideText(L);
  out.push('# ' + mdEscape(guide.title.trim() || t.guide));
  if (guide.description.trim()) out.push(lines(guide.description));
  out.push(
    `_${t.steps(count)} · ${t.aboutMin(estimatedMinutes(guide))}_`,
  );
  for (const s of guide.steps) {
    if (s.kind === 'text') {
      const tone = s.tone || 'info';
      if (tone === 'heading') out.push('## ' + mdEscape(s.title.trim() || t.heading));
      else {
        const head = `> **${mdEscape(s.title.trim() || t[tone])}**`;
        const body = s.description.trim()
          ? '\n> ' + lines(s.description).replace(/\n/g, '\n> ')
          : '';
        out.push(head + body);
      }
      continue;
    }
    const n = numbers.get(s.id)!;
    out.push(`### ${n}. ${mdEscape(stepTitle(s, L))}`);
    if (s.description.trim()) out.push(lines(s.description));
    const href = cleanUrl(s.url);
    if ((s.kind === 'navigate' || s.kind === 'tab') && href)
      out.push(`[${mdEscape(displayUrl(s.url))}](<${href}>)`);
    const img = images[s.id];
    if (img) out.push(`![${t.step} ${n}](${img})`);
  }
  out.push('---\n_' + t.madeWith + '_');
  return out.join('\n\n') + '\n';
}
